import { supabase } from '../../supabaseClient';
import { fetchAllRows } from './supabaseFetchAll';

// Redistribui `newTotal` entre as categorias do breakdown de forma proporcional
// aos valores atuais (usados como pesos), com a última categoria de peso > 0
// absorvendo o resto do arredondamento → a soma das linhas fecha EXATA (em
// centavos) com `newTotal`. Usado no re-rateio da DRE quando o total da cobrança
// muda na edição. Função pura.
export const rescaleBreakdown = (rows, newTotal) => {
  if (!rows || rows.length === 0) return [];

  const totalCents = Math.round((Number(newTotal) || 0) * 100);
  const weights = rows.map((r) => Number(r.value) || 0);
  const totalWeight = weights.reduce((s, w) => s + w, 0);
  const lastIdx = weights.reduce((last, w, i) => (w > 0 ? i : last), -1);

  let acc = 0;
  return rows.map((row, i) => {
    let cents;
    if (totalWeight <= 0) {
      // Sem pesos válidos: distribui igualmente (fallback defensivo).
      const base = Math.floor(totalCents / rows.length);
      cents = i === rows.length - 1 ? totalCents - acc : base;
    } else if (i === lastIdx) {
      cents = totalCents - acc;
    } else {
      cents = Math.round((totalCents * weights[i]) / totalWeight);
    }
    acc += cents;
    return { ...row, value: cents / 100 };
  });
};

// Linhas de breakdown (não-deletadas) de um lançamento, com o AccountPlan.
export const loadBreakdownByEntry = async (financialEntryId) => {
  const { data, error } = await supabase
    .from('FinancialEntryBreakdown')
    .select('*, AccountPlan(*)')
    .eq('financial_entry_id', financialEntryId)
    .is('deleted_at', null)
    .order('id', { ascending: true });
  if (error || !data) return [];
  return data;
};

// Breakdown de vários lançamentos de uma vez (DRE do período). Traz o AccountPlan
// com os campos que a árvore da DRE usa.
export const loadBreakdownByEntries = async (financialEntryIds) => {
  if (!financialEntryIds || financialEntryIds.length === 0) return [];
  const { data, error } = await fetchAllRows(() =>
    supabase
      .from('FinancialEntryBreakdown')
      .select('id, financial_entry_id, value, AccountPlan(id, code, name, type, parent_id, level)')
      .in('financial_entry_id', financialEntryIds)
      .is('deleted_at', null)
      .order('id', { ascending: true })
  );
  if (error || !data) return [];
  return data;
};

export const saveBreakdownRows = async (financialEntryId, rows, userId) => {
  const payloads = rows.map((r) => ({
    financial_entry_id: financialEntryId,
    account_plan_id: r.account_plan_id || null,
    value: Number(r.value) || 0,
    created_by: userId ?? null,
  }));
  return await supabase.from('FinancialEntryBreakdown').insert(payloads);
};

export const softDeleteBreakdownByEntry = async (financialEntryId, userId) => {
  return await supabase
    .from('FinancialEntryBreakdown')
    .update({ deleted_at: new Date(), deleted_by: userId ?? null })
    .eq('financial_entry_id', financialEntryId)
    .is('deleted_at', null);
};

// Substitui o breakdown de um lançamento: soft-delete das linhas atuais + insere
// as novas. Usado na edição quando o total muda (re-rateio da DRE).
export const replaceBreakdownRows = async (financialEntryId, rows, userId) => {
  const { error: delErr } = await softDeleteBreakdownByEntry(financialEntryId, userId);
  if (delErr) return { error: delErr };
  return await saveBreakdownRows(financialEntryId, rows, userId);
};
